import React, { useEffect, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import { Box } from '@mui/material';
import { TOOLTIP_PLACEMENT } from './TableSpotlight';

interface CustomTooltipProps {
    targetSelector: string;
    content: React.ReactNode;
    position?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    offset?: [number, number];
    zIndex?: number;
    tooltipPlacement?: TOOLTIP_PLACEMENT;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
    targetSelector,
    content,
    align = 'center',
    offset = [10, 0],
    zIndex = 9999,
    tooltipPlacement = "right-start" as TOOLTIP_PLACEMENT
}) => {
    const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
    const [paperContainer, setPaperContainer] = useState<HTMLElement | null>(null);

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        let mutationObserver: MutationObserver | null = null;

        const updateRect = () => {
            const element = document.querySelector(targetSelector) as HTMLElement;
            const container = document.querySelector('.MuiDialog-container .MuiPaper-root') as HTMLElement;
            
            if (element && container) {
                setTargetElement(element);
                setPaperContainer(container);
            }
        };

        // Initial update
        updateRect();

        // Setup observers
        const setupObservers = () => {
            const element = document.querySelector(targetSelector) as HTMLElement;
            const container = document.querySelector('.MuiDialog-container .MuiPaper-root') as HTMLElement;

            // Cleanup existing observers
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (mutationObserver) {
                mutationObserver.disconnect();
            }

            if (element && container) {
                // Setup resize observer
                resizeObserver = new ResizeObserver(updateRect);
                resizeObserver.observe(element);
                resizeObserver.observe(container);
                resizeObserver.observe(document.body);

                // Setup mutation observer for DOM changes
                mutationObserver = new MutationObserver(updateRect);
                mutationObserver.observe(document.body, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                    attributeFilter: ['style', 'class']
                });
            }
        };

        // Initial observer setup
        setupObservers();

        // Handle window resize
        const handleResize = () => {
            setupObservers();
            updateRect();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [targetSelector]);

    if (!targetElement || !paperContainer) return null;

    return (
        <Tooltip
            open
            placement={tooltipPlacement}
            title={
                <Paper sx={{ borderRadius: "12px" }}>
                    {content}
                </Paper>
            }
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: "#FFF",
                        zIndex: zIndex,
                        borderRadius: "12px",
                        padding: 0,
                        '& .MuiTooltip-arrow': {
                            color: '#FFF',
                            marginTop: '10px',
                        },
                        maxWidth: "350px",
                    },
                },
                arrow: {
                    sx: {
                        color: "#fff",
                        zIndex: zIndex,
                    },
                },
            }}
            PopperProps={{
                anchorEl: paperContainer,
                disablePortal: false,
                container: document.body,
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [(paperContainer.getBoundingClientRect().height / 1.4) - 10, 0],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altAxis: true,
                            altBoundary: true,
                            tether: true,
                            rootBoundary: 'document',
                            padding: 8,
                        },
                    },
                    {
                        name: 'zIndexFix',
                        enabled: true,
                        phase: 'beforeWrite',
                        fn: ({ state }) => {
                            state.styles.popper.zIndex = zIndex.toString();
                        },
                    },
                ],
            }}
        >
            <span style={{ position: "absolute", top: -9999 }} />
        </Tooltip>
    );
};

export default CustomTooltip; 